import { useState, useEffect } from 'react'
import type { DiagnosisCode } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'

interface Form {
  code: string
  description: string
  parentCode: string
  isBillable: boolean
}

const defaults: Form = { code: '', description: '', parentCode: '', isBillable: true }

interface Props {
  readonly codeId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (d: DiagnosisCode) => void
  readonly onArchived?: (id: string) => void
}

export function DiagnosisCodeEditModal({ codeId, isOpen, isNew, onClose, onSaved, onArchived }: Props) {
  const [form, setForm] = useState<Form>(defaults)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSystem, setIsSystem] = useState(false)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setError(null)
      setIsSystem(false)
      return
    }
    if (!codeId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<DiagnosisCode>(`/diagnosis-codes/${codeId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) {
          const d = res.data
          setForm({
            code: d.code,
            description: d.description,
            parentCode: d.parentCode ?? '',
            isBillable: d.isBillable,
          })
          setIsSystem(d.isSystem)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [codeId, isOpen, isNew])

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((prev) => ({ ...prev, [k]: v }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = isNew
        ? {
            code: form.code,
            description: form.description,
            isBillable: form.isBillable,
          }
        : { description: form.description, isBillable: form.isBillable }
      if (form.parentCode) payload.parentCode = form.parentCode
      const res = isNew
        ? await api.post<DiagnosisCode>('/diagnosis-codes', payload)
        : await api.put<DiagnosisCode>(`/diagnosis-codes/${codeId}`, payload)
      if (!res.data) throw new Error('No data returned')
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!codeId) return
    setArchiving(true)
    try {
      await api.delete(`/diagnosis-codes/${codeId}`)
      onArchived?.(codeId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const title = isNew ? 'New Diagnosis Code' : `${form.code} - ${form.description.slice(0, 60)}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="640px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            {isSystem && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  marginBottom: '1rem',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}
                data-testid="system-record-notice"
              >
                This is a system-provided diagnosis code and cannot be edited or archived.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <FormField
                name="code"
                label="ICD-10 Code"
                required
                value={form.code}
                onChange={(v) => update('code', v.toUpperCase())}
                disabled={!isNew || isSystem}
              />
              <FormField
                name="parentCode"
                label="Parent (optional)"
                value={form.parentCode}
                onChange={(v) => update('parentCode', v.toUpperCase())}
                disabled={isSystem}
              />
            </div>
            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="description" label="Description" required>
                <textarea
                  style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  disabled={isSystem}
                />
              </FormField>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <label style={{ display: 'flex', gap: '0.5rem', fontSize: '0.9375rem' }}>
                <input
                  type="checkbox"
                  checked={form.isBillable}
                  onChange={(e) => update('isBillable', e.target.checked)}
                  disabled={isSystem}
                />
                Billable code (acceptable on a claim)
              </label>
            </div>
            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && !isSystem && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                >
                  Archive
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                {isSystem ? 'Close' : 'Cancel'}
              </button>
              {!isSystem && (
                <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Diagnosis Code"
        message={`Archive ${form.code}? Existing orders referencing it are preserved but new orders cannot pick it.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
